import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  createCustomerShippingAddress,
  deleteCustomerShippingAddress,
  getCustomerShippingAddresses,
  getCustomers,
  getRouteAreaMappings,
  updateCustomerShippingAddress,
} from "../../api/client.js";

import {
  ConfirmDialog,
  Dialog,
} from "../Dialog.jsx";

import { SortableHeader } from "../SortableHeader.jsx";
import { getNextTableSort, useTableSort } from "../../hooks/useTableSort.js";
import {
  DataMasterActiveFilters,
  DataMasterKpiCard,
  DataMasterKpiStrip,
  DataMasterPagination,
  DataMasterRowActions,
  DataMasterSection,
  DataMasterTableShell,
  DataMasterToolbar,
} from "./DataMasterLayout.jsx";

const PAGE_SIZE = 50;

const EMPTY_ADDRESS_FILTERS = {
  search: "",
  coordinateStatus: "",
  activeStatus: "",
  area: "",
  routeCode: "",
};

const EMPTY_ADDRESS_SUMMARY = {
  total: 0,
  customers: 0,
  active: 0,
  valid: 0,
  review: 0,
  missing: 0,
};

const EMPTY_FORM = {
  customerMasterId: null,
  accurateCustomerId: null,
  customerNo: "",
  customerName: "",
  addressAlias: "",
  addressText: "",
  resolvedArea: "",
  resolvedRoute: "",
  resolvedRouteName: "",
  keywordText: "",
  latitude: "",
  longitude: "",
  coordinateStatus: "MISSING",
  active: true,
  notes: "",
};

const numberFormatter =
  new Intl.NumberFormat("id-ID");

function formatCoordinate(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return "—";
  }

  return numeric.toFixed(7).replace(/0+$/, "").replace(/\.$/, "");
}

function normalizeCoordinateStatus(value) {
  const normalized = String(value || "MISSING").toUpperCase();

  return ["VALID", "NEEDS_REVIEW", "MISSING"].includes(normalized)
    ? normalized
    : "MISSING";
}

function CoordinateStatusBadge({ status }) {
  const normalized = normalizeCoordinateStatus(status);

  const labels = {
    VALID: "Valid",
    NEEDS_REVIEW: "Perlu diperiksa",
    MISSING: "Belum tersedia",
  };

  return (
    <span
      className={`coordinate-status coordinate-status--${normalized
        .toLowerCase()
        .replaceAll("_", "-")}`}
    >
      {labels[normalized]}
    </span>
  );
}

function ActiveBadge({ active }) {
  return (
    <span
      className={`master-active master-active--${
        active ? "yes" : "no"
      }`}
    >
      {active ? "Aktif" : "Nonaktif"}
    </span>
  );
}

function ActiveSelect({
  onChange,
  value,
}) {
  return (
    <select
      aria-label="Status"
      onChange={(event) =>
        onChange(
          event.target.value === "active",
        )
      }
      value={value ? "active" : "inactive"}
    >
      <option value="active">Aktif</option>
      <option value="inactive">Nonaktif</option>
    </select>
  );
}

function Feedback({
  error,
  message,
}) {
  return (
    <>
      {message && (
        <div className="notice-banner">
          {message}
        </div>
      )}

      {error && (
        <div
          className="error-banner"
          role="alert"
        >
          {error}
        </div>
      )}
    </>
  );
}

function ModalActions({
  busy,
  onCancel,
}) {
  return (
    <footer className="dialog-card__actions">
      <button
        className="secondary-button"
        disabled={busy}
        onClick={onCancel}
        type="button"
      >
        Batal
      </button>

      <button
        className="primary-button"
        disabled={busy}
        type="submit"
      >
        {busy
          ? "Menyimpan…"
          : "Simpan Perubahan"}
      </button>
    </footer>
  );
}

function normalizeMapping(row) {
  return {
    id: row.id,
    areaName: String(
      row.areaName || "",
    ).trim(),
    routeCode: String(
      row.routeCode || "",
    ).trim(),
    routeName: String(
      row.routeName ||
        row.routeCode ||
        "",
    ).trim(),
    active: row.active !== false,
  };
}

function mapRowToForm(row) {
  const hasCoordinates =
    row.latitude !== null &&
    row.latitude !== undefined &&
    row.longitude !== null &&
    row.longitude !== undefined;

  return {
    id: row.id,
    customerMasterId:
      row.customerMasterId || null,
    accurateCustomerId:
      row.accurateCustomerId || null,
    customerNo:
      row.customerNo || "",
    customerName:
      row.customerName || "",
    addressAlias:
      row.addressAlias || "",
    addressText:
      row.addressText || "",
    resolvedArea:
      row.resolvedArea || "",
    resolvedRoute:
      row.resolvedRoute || "",
    resolvedRouteName:
      row.resolvedRouteName || "",
    keywordText:
      row.keywordText || "",
    latitude:
      hasCoordinates
        ? String(row.latitude)
        : "",
    longitude:
      hasCoordinates
        ? String(row.longitude)
        : "",
    coordinateStatus:
      hasCoordinates
        ? normalizeCoordinateStatus(
            row.coordinateStatus,
          ) === "VALID"
          ? "VALID"
          : "NEEDS_REVIEW"
        : "MISSING",
    active:
      row.active !== false,
    notes:
      row.notes || "",
  };
}

function buildPayload(value) {
  const latitude =
    String(value.latitude ?? "").trim();

  const longitude =
    String(value.longitude ?? "").trim();

  const hasCoordinates =
    Boolean(latitude && longitude);

  return {
    customerMasterId:
      value.customerMasterId,
    accurateCustomerId:
      value.accurateCustomerId,
    customerNo:
      value.customerNo,
    customerName:
      value.customerName,
    addressAlias:
      value.addressAlias.trim(),
    addressText:
      String(value.addressText || "").trim(),
    resolvedArea:
      value.resolvedArea,
    keywordText:
      value.keywordText.trim(),
    latitude:
      hasCoordinates
        ? latitude
        : null,
    longitude:
      hasCoordinates
        ? longitude
        : null,
    coordinateSource:
      hasCoordinates
        ? "MANUAL"
        : null,
    coordinateStatus:
      hasCoordinates
        ? value.coordinateStatus === "VALID"
          ? "VALID"
          : "NEEDS_REVIEW"
        : "MISSING",
    active:
      value.active,
    notes:
      String(value.notes || "").trim(),
  };
}

function getRouteDisplay(value) {
  if (value.resolvedRouteName) {
    return value.resolvedRouteName;
  }

  if (value.resolvedRoute) {
    return value.resolvedRoute;
  }

  return "";
}

function validateCoordinatePair(value) {
  const latitudeFilled =
    String(value.latitude ?? "").trim() !== "";

  const longitudeFilled =
    String(value.longitude ?? "").trim() !== "";

  if (latitudeFilled !== longitudeFilled) {
    return "Latitude dan longitude wajib diisi bersamaan.";
  }

  if (!latitudeFilled) {
    return "";
  }

  const latitude = Number(
    String(value.latitude).replace(",", "."),
  );

  const longitude = Number(
    String(value.longitude).replace(",", "."),
  );

  if (
    !Number.isFinite(latitude) ||
    latitude < -90 ||
    latitude > 90
  ) {
    return "Latitude harus berupa angka antara -90 sampai 90.";
  }

  if (
    !Number.isFinite(longitude) ||
    longitude < -180 ||
    longitude > 180
  ) {
    return "Longitude harus berupa angka antara -180 sampai 180.";
  }

  return "";
}

function CoordinateInputs({
  compact = false,
  onChange,
  value,
}) {
  const hasCoordinates =
    Boolean(
      String(value.latitude ?? "").trim() &&
        String(value.longitude ?? "").trim(),
    );

  function updateCoordinate(field, nextValue) {
    const next = {
      ...value,
      [field]: nextValue,
    };

    const pairFilled =
      Boolean(
        String(next.latitude ?? "").trim() &&
          String(next.longitude ?? "").trim(),
      );

    next.coordinateStatus = pairFilled
      ? next.coordinateStatus === "MISSING"
        ? "NEEDS_REVIEW"
        : next.coordinateStatus
      : "MISSING";

    onChange(next);
  }

  return (
    <div
      className={
        compact
          ? "coordinate-inputs coordinate-inputs--compact"
          : "coordinate-inputs"
      }
    >
      <label>
        <span>Latitude</span>

        <input
          inputMode="decimal"
          onChange={(event) =>
            updateCoordinate(
              "latitude",
              event.target.value,
            )
          }
          placeholder="-6.123456"
          value={value.latitude}
        />
      </label>

      <label>
        <span>Longitude</span>

        <input
          inputMode="decimal"
          onChange={(event) =>
            updateCoordinate(
              "longitude",
              event.target.value,
            )
          }
          placeholder="110.123456"
          value={value.longitude}
        />
      </label>

      <label>
        <span>Status Koordinat</span>

        <select
          disabled={!hasCoordinates}
          onChange={(event) =>
            onChange({
              ...value,
              coordinateStatus:
                event.target.value,
            })
          }
          value={value.coordinateStatus}
        >
          <option value="NEEDS_REVIEW">
            Perlu diperiksa
          </option>

          <option value="VALID">
            Valid
          </option>

          {!hasCoordinates && (
            <option value="MISSING">
              Belum tersedia
            </option>
          )}
        </select>
      </label>
    </div>
  );
}

function CustomerLookup({
  customerNo,
  disabled = false,
  onClear,
  onSelect,
  value,
}) {
  const [query, setQuery] =
    useState(value || "");

  const [options, setOptions] =
    useState([]);

  const [open, setOpen] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [searchError, setSearchError] =
    useState("");
  const [highlightedIndex, setHighlightedIndex] =
    useState(-1);
  const listboxId = useRef(
    `customer-lookup-${Math.random().toString(36).slice(2)}`,
  );

  useEffect(() => {
    if (!open) {
      setQuery(value || "");
    }
  }, [open, value]);

  useEffect(() => {
    if (!open || disabled) {
      return undefined;
    }

    let cancelled = false;

    const timer =
      window.setTimeout(
        async () => {
          const searchText = query.trim();

          if (searchText.length < 2) {
            setOptions([]);
            setLoading(false);
            setSearchError("");
            setHighlightedIndex(-1);
            return;
          }

          setLoading(true);
          setSearchError("");

          try {
            const payload =
              await getCustomers({
                page: 1,
                pageSize: 50,
                search:
                  searchText,
              });

            if (!cancelled) {
              setOptions(
                payload.customers || [],
              );
              setHighlightedIndex(
                (payload.customers || []).length
                  ? 0
                  : -1,
              );
            }
          } catch (requestError) {
            if (!cancelled) {
              setOptions([]);
              setSearchError(
                requestError.message,
              );
            }
          } finally {
            if (!cancelled) {
              setLoading(false);
            }
          }
        },
        250,
      );

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    disabled,
    open,
    query,
  ]);

  function handleInputChange(event) {
    const nextValue =
      event.target.value;

    setQuery(nextValue);
    setOpen(true);
    setHighlightedIndex(-1);

    if (nextValue !== value) {
      onClear();
    }
  }

  function selectCustomer(customer) {
    onSelect(customer);

    setQuery(
      customer.customerName || "",
    );

    setOpen(false);
    setHighlightedIndex(-1);
  }

  function handleKeyDown(event) {
    if (!open && ["ArrowDown", "ArrowUp"].includes(event.key)) {
      setOpen(true);
      return;
    }

    if (!open) return;

    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((current) =>
        options.length ? Math.min(current + 1, options.length - 1) : -1,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((current) =>
        options.length ? Math.max(current - 1, 0) : -1,
      );
      return;
    }

    if (event.key === "Enter" && highlightedIndex >= 0 && options[highlightedIndex]) {
      event.preventDefault();
      selectCustomer(options[highlightedIndex]);
    }
  }

  function handleBlur() {
    window.setTimeout(() => {
      setOpen(false);
    }, 150);
  }

  return (
    <div className="customer-lookup">
      <input
        aria-autocomplete="list"
        aria-activedescendant={
          highlightedIndex >= 0
            ? `${listboxId.current}-option-${highlightedIndex}`
            : undefined
        }
        aria-controls={listboxId.current}
        aria-expanded={open}
        aria-label="Nama Pelanggan"
        autoComplete="off"
        disabled={disabled}
        onBlur={handleBlur}
        onChange={handleInputChange}
        onFocus={() =>
          setOpen(true)
        }
        onKeyDown={handleKeyDown}
        placeholder="Cari Nama Pelanggan"
        role="combobox"
        value={query}
      />

      {customerNo && (
        <span className="customer-lookup__selected-number">
          {customerNo}
        </span>
      )}

      {open && !disabled && (
        <div
          className="customer-lookup__menu"
          id={listboxId.current}
          role="listbox"
        >
          {query.trim().length < 2 && (
            <div className="customer-lookup__message">
              Ketik minimal 2 karakter untuk mencari pelanggan.
            </div>
          )}

          {loading && (
            <div className="customer-lookup__message">
              Mencari pelanggan…
            </div>
          )}

          {!loading &&
            searchError && (
              <div className="customer-lookup__message customer-lookup__message--error">
                {searchError}
              </div>
            )}

          {!loading &&
            !searchError &&
            query.trim().length >= 2 &&
            options.map(
              (customer, index) => (
                <button
                  aria-selected={highlightedIndex === index}
                  className={
                    highlightedIndex === index
                      ? "is-highlighted"
                      : ""
                  }
                  id={`${listboxId.current}-option-${index}`}
                  key={customer.id}
                  onClick={() =>
                    selectCustomer(
                      customer,
                    )
                  }
                  onMouseDown={(event) =>
                    event.preventDefault()
                  }
                  role="option"
                  type="button"
                >
                  <strong>
                    {
                      customer.customerName
                    }
                  </strong>

                  <span>
                    {customer.customerNo ||
                      "Tanpa nomor"}

                    {customer.billingCity
                      ? ` · ${customer.billingCity}`
                      : ""}
                  </span>
                </button>
              ),
            )}

          {!loading &&
            !searchError &&
            query.trim().length >= 2 &&
            options.length === 0 && (
              <div className="customer-lookup__message">
                Pelanggan tidak ditemukan.
              </div>
            )}
        </div>
      )}
    </div>
  );
}

export function CustomerShippingAddressesTab() {
  const [addresses, setAddresses] =
    useState([]);

  const [mappings, setMappings] =
    useState([]);

  const [pagination, setPagination] =
    useState({
      page: 1,
      pageSize: PAGE_SIZE,
      pageCount: 1,
      rowCount: 0,
    });

  const [filters, setFilters] =
    useState(EMPTY_ADDRESS_FILTERS);

  const [draftFilters, setDraftFilters] =
    useState(EMPTY_ADDRESS_FILTERS);

  const [summary, setSummary] =
    useState(EMPTY_ADDRESS_SUMMARY);

  const [form, setForm] =
    useState(EMPTY_FORM);
  const [createOpen, setCreateOpen] =
    useState(false);

  const [editRow, setEditRow] =
    useState(null);

  const [deleteRow, setDeleteRow] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [busy, setBusy] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const [error, setError] =
    useState("");

  const { sort, setSort } = useTableSort("customerName");

  const activeMappings =
    useMemo(() => {
      const seen =
        new Set();

      return mappings
        .map(normalizeMapping)
        .filter((mapping) => {
          if (
            !mapping.active ||
            !mapping.areaName ||
            !mapping.routeCode
          ) {
            return false;
          }

          const key =
            mapping.areaName.toLowerCase();

          if (seen.has(key)) {
            return false;
          }

          seen.add(key);
          return true;
        })
        .sort((left, right) =>
          left.areaName.localeCompare(
            right.areaName,
            "id",
          ),
        );
    }, [mappings]);

  const mappingByArea =
    useMemo(
      () =>
        new Map(
          activeMappings.map(
            (mapping) => [
              mapping.areaName.toLowerCase(),
              mapping,
            ],
          ),
        ),
      [activeMappings],
    );


  const routeOptions = useMemo(() => {
    const seen = new Set();

    return activeMappings.filter((mapping) => {
      const key = mapping.routeCode.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [activeMappings]);

  const loadAddresses =
    useCallback(
      async (
        page = 1,
        nextFilters = EMPTY_ADDRESS_FILTERS,
        nextSort = sort,
        nextPageSize = pagination.pageSize,
      ) => {
        setLoading(true);
        setError("");

        try {
          const payload =
            await getCustomerShippingAddresses({
              page,
              pageSize: nextPageSize,
              ...nextFilters,
              sortBy: nextSort.key,
              sortDir: nextSort.direction,
            });

          setAddresses(payload.addresses || []);
          setSummary(payload.summary || EMPTY_ADDRESS_SUMMARY);
          setPagination(
            payload.pagination || {
              page,
              pageSize: nextPageSize,
              pageCount: 1,
              rowCount: 0,
            },
          );
        } catch (requestError) {
          setError(requestError.message);
        } finally {
          setLoading(false);
        }
      },
      [pagination.pageSize, sort],
    );

  const loadMappings =
    useCallback(async () => {
      try {
        const payload =
          await getRouteAreaMappings();

        setMappings(
          payload.mappings || [],
        );
      } catch (requestError) {
        setMappings([]);
        setError(
          requestError.message,
        );
      }
    }, []);

  useEffect(() => {
    loadAddresses(1, EMPTY_ADDRESS_FILTERS);
    loadMappings();
    // Initial workspace hydration only. Subsequent sorting, paging, and filtering
    // explicitly preserve the currently applied filter state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetAddForm() {
    setForm(EMPTY_FORM);
  }

  function selectCustomerForAdd(customer) {
    setForm((current) => ({
      ...current,
      customerMasterId:
        customer.id,
      accurateCustomerId:
        customer.accurateCustomerId ||
        null,
      customerNo:
        customer.customerNo || "",
      customerName:
        customer.customerName || "",
    }));
  }

  function clearCustomerForAdd() {
    setForm((current) => ({
      ...current,
      customerMasterId: null,
      accurateCustomerId: null,
      customerNo: "",
      customerName: "",
    }));
  }

  function selectCustomerForEdit(customer) {
    setEditRow((current) => ({
      ...current,
      customerMasterId:
        customer.id,
      accurateCustomerId:
        customer.accurateCustomerId ||
        null,
      customerNo:
        customer.customerNo || "",
      customerName:
        customer.customerName || "",
    }));
  }

  function clearCustomerForEdit() {
    setEditRow((current) => ({
      ...current,
      customerMasterId: null,
      accurateCustomerId: null,
      customerNo: "",
      customerName: "",
    }));
  }

  function setAreaForAdd(areaName) {
    const mapping =
      mappingByArea.get(
        areaName.toLowerCase(),
      );

    setForm((current) => ({
      ...current,
      resolvedArea:
        mapping?.areaName || "",
      resolvedRoute:
        mapping?.routeCode || "",
      resolvedRouteName:
        mapping?.routeName || "",
    }));
  }

  function setAreaForEdit(areaName) {
    const mapping =
      mappingByArea.get(
        areaName.toLowerCase(),
      );

    setEditRow((current) => ({
      ...current,
      resolvedArea:
        mapping?.areaName || "",
      resolvedRoute:
        mapping?.routeCode || "",
      resolvedRouteName:
        mapping?.routeName || "",
    }));
  }

  function validateForm(value) {
    if (!value.customerMasterId) {
      return "Pilih Nama Pelanggan dari master Pelanggan.";
    }

    if (!value.addressAlias.trim()) {
      return "Nama Alamat wajib diisi.";
    }

    if (
      !value.resolvedArea ||
      !value.resolvedRoute
    ) {
      return "Pilih Area Kirim yang sudah tersedia di Mapping Area.";
    }

    return validateCoordinatePair(value);
  }

  function submitSearch(event) {
    event.preventDefault();

    const nextFilters = {
      ...draftFilters,
      search: draftFilters.search.trim(),
    };

    setFilters(nextFilters);
    loadAddresses(1, nextFilters, sort);
  }

  function clearSearch() {
    setDraftFilters(EMPTY_ADDRESS_FILTERS);
    setFilters(EMPTY_ADDRESS_FILTERS);
    loadAddresses(1, EMPTY_ADDRESS_FILTERS, sort);
  }

  function toggleSort(column) {
    const nextSort = getNextTableSort(sort, column);
    setSort(nextSort);
    loadAddresses(1, filters, nextSort);
  }

  function changePageSize(pageSize) {
    loadAddresses(1, filters, sort, pageSize);
  }

  async function addAddress(event) {
    event.preventDefault();

    const validationError =
      validateForm(form);

    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setMessage("");
    setError("");

    try {
      const response =
        await createCustomerShippingAddress(
          buildPayload(form),
        );

      setMessage(
        response.message ||
          "Alamat kirim pelanggan berhasil ditambahkan.",
      );

      resetAddForm();
      setCreateOpen(false);

      await loadAddresses(
        1,
        filters,
        sort,
      );
    } catch (requestError) {
      setError(
        requestError.message,
      );
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(event) {
    event.preventDefault();

    const validationError =
      validateForm(editRow);

    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setMessage("");
    setError("");

    try {
      const response =
        await updateCustomerShippingAddress(
          editRow.id,
          buildPayload(editRow),
        );

      setMessage(
        response.message ||
          "Alamat kirim pelanggan berhasil diperbarui.",
      );

      setEditRow(null);

      await loadAddresses(
        pagination.page,
        filters,
        sort,
      );
    } catch (requestError) {
      setError(
        requestError.message,
      );
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(row) {
    setMessage("");
    setError("");

    try {
      const response =
        await updateCustomerShippingAddress(
          row.id,
          buildPayload({
            ...mapRowToForm(row),
            active: !row.active,
          }),
        );

      setMessage(
        response.message ||
          `Alamat kirim berhasil ${
            row.active
              ? "dinonaktifkan"
              : "diaktifkan"
          }.`,
      );

      await loadAddresses(
        pagination.page,
        filters,
        sort,
      );
    } catch (requestError) {
      setError(
        requestError.message,
      );
    }
  }

  async function confirmDelete() {
    setBusy(true);
    setMessage("");
    setError("");

    try {
      const response =
        await deleteCustomerShippingAddress(
          deleteRow.id,
        );

      setMessage(
        response.message ||
          "Alamat kirim pelanggan berhasil dihapus.",
      );

      setDeleteRow(null);

      await loadAddresses(
        pagination.page,
        filters,
        sort,
      );
    } catch (requestError) {
      setError(
        requestError.message,
      );

      setDeleteRow(null);
    } finally {
      setBusy(false);
    }
  }

  function changePage(page) {
    if (
      page < 1 ||
      page > pagination.pageCount ||
      loading
    ) {
      return;
    }

    loadAddresses(
      page,
      filters,
      sort,
    );
  }

  return (
    <DataMasterSection
      actions={
        <button
          className="primary-button"
          onClick={() => {
            resetAddForm();
            setCreateOpen(true);
          }}
          type="button"
        >
          Tambah Alamat
        </button>
      }
      className="shipping-address-master"
      error={createOpen || editRow ? "" : error}
      message={message}
      subtitle="Kelola tujuan kirim tambahan, area, rute, dan kesiapan koordinat pelanggan."
      title="Alamat Kirim"
    >
      <DataMasterKpiStrip label="Ringkasan alamat kirim">
        <DataMasterKpiCard
          helper="Hasil filter aktif"
          icon="database"
          label="Total alamat"
          value={summary.total}
        />
        <DataMasterKpiCard
          helper="Pelanggan unik"
          icon="user"
          label="Pelanggan"
          tone="info"
          value={summary.customers}
        />
        <DataMasterKpiCard
          helper="Siap dipakai operasional"
          icon="check"
          label="Koordinat valid"
          tone="success"
          value={summary.valid}
        />
        <DataMasterKpiCard
          helper={`${summary.missing} belum tersedia`}
          icon="warning"
          label="Perlu perhatian"
          tone="warning"
          value={summary.review + summary.missing}
        />
        <DataMasterKpiCard
          helper="Alamat berstatus aktif"
          icon="location"
          label="Alamat aktif"
          value={summary.active}
        />
      </DataMasterKpiStrip>

      <DataMasterToolbar className="dm-location-filter-bar" onSubmit={submitSearch}>
        <input
          aria-label="Cari alamat kirim pelanggan"
          onChange={(event) =>
            setDraftFilters((current) => ({
              ...current,
              search: event.target.value,
            }))
          }
          placeholder="Cari pelanggan, nama alamat, alamat, atau keyword"
          type="search"
          value={draftFilters.search}
        />

        <select
          aria-label="Status koordinat alamat kirim"
          onChange={(event) =>
            setDraftFilters((current) => ({
              ...current,
              coordinateStatus: event.target.value,
            }))
          }
          value={draftFilters.coordinateStatus}
        >
          <option value="">Semua status koordinat</option>
          <option value="VALID">Valid</option>
          <option value="NEEDS_REVIEW">Perlu diperiksa</option>
          <option value="MISSING">Belum tersedia</option>
        </select>

        <select
          aria-label="Status alamat kirim"
          onChange={(event) =>
            setDraftFilters((current) => ({
              ...current,
              activeStatus: event.target.value,
            }))
          }
          value={draftFilters.activeStatus}
        >
          <option value="">Semua status data</option>
          <option value="ACTIVE">Aktif</option>
          <option value="INACTIVE">Nonaktif</option>
        </select>

        <select
          aria-label="Area kirim"
          onChange={(event) =>
            setDraftFilters((current) => ({
              ...current,
              area: event.target.value,
            }))
          }
          value={draftFilters.area}
        >
          <option value="">Semua area</option>
          {activeMappings.map((mapping) => (
            <option key={mapping.id} value={mapping.areaName}>
              {mapping.areaName}
            </option>
          ))}
        </select>

        <select
          aria-label="Rute alamat kirim"
          onChange={(event) =>
            setDraftFilters((current) => ({
              ...current,
              routeCode: event.target.value,
            }))
          }
          value={draftFilters.routeCode}
        >
          <option value="">Semua rute</option>
          {routeOptions.map((mapping) => (
            <option key={mapping.routeCode} value={mapping.routeCode}>
              {mapping.routeName || mapping.routeCode}
            </option>
          ))}
        </select>

        <button className="secondary-button" disabled={loading} type="submit">
          Terapkan
        </button>
        <button
          className="text-button"
          disabled={loading}
          onClick={clearSearch}
          type="button"
        >
          Bersihkan
        </button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          {
            label: "Koordinat",
            value: {
              VALID: "Valid",
              NEEDS_REVIEW: "Perlu diperiksa",
              MISSING: "Belum tersedia",
            }[filters.coordinateStatus] || "",
          },
          {
            label: "Status",
            value: {
              ACTIVE: "Aktif",
              INACTIVE: "Nonaktif",
            }[filters.activeStatus] || "",
          },
          { label: "Area", value: filters.area },
          {
            label: "Rute",
            value:
              routeOptions.find((item) => item.routeCode === filters.routeCode)
                ?.routeName || filters.routeCode,
          },
        ]}
        onClear={clearSearch}
      />

      {createOpen && (
        <Dialog
          className="dialog-card--shipping-editor"
          onClose={() => {
            if (!busy) setCreateOpen(false);
          }}
          title="Tambah Alamat Kirim"
        >
          <form className="dialog-form" onSubmit={addAddress}>
            <div className="dialog-card__body grouped-master-form">
              {error && (
                <div className="error-banner" role="alert">
                  {error}
                </div>
              )}

              <section className="master-form-group">
                <h3>Pelanggan</h3>
                <div className="dialog-form__grid">
                  <div className="dialog-field dialog-form__full">
                    <span>Cari dan pilih pelanggan</span>
                    <CustomerLookup
                      customerNo={form.customerNo}
                      onClear={clearCustomerForAdd}
                      onSelect={selectCustomerForAdd}
                      value={form.customerName}
                    />
                    <small>Pilih dari master pelanggan agar identitas pelanggan tersimpan benar.</small>
                  </div>
                </div>
              </section>

              <section className="master-form-group">
                <h3>Identitas Alamat</h3>
                <div className="dialog-form__grid aligned-form-grid">
                  <label>
                    <span>Nama Alamat</span>
                    <input
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          addressAlias: event.target.value,
                        }))
                      }
                      required
                      value={form.addressAlias}
                    />
                  </label>

                  <label>
                    <span>Keyword</span>
                    <input
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          keywordText: event.target.value,
                        }))
                      }
                      value={form.keywordText}
                    />
                  </label>

                  <label className="dialog-form__full">
                    <span>Alamat</span>
                    <textarea
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          addressText: event.target.value,
                        }))
                      }
                      rows={3}
                      value={form.addressText}
                    />
                  </label>

                  <label className="dialog-form__full">
                    <span>Catatan</span>
                    <textarea
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          notes: event.target.value,
                        }))
                      }
                      rows={2}
                      value={form.notes}
                    />
                  </label>

                  <label>
                    <span>Status</span>
                    <ActiveSelect
                      onChange={(active) =>
                        setForm((current) => ({
                          ...current,
                          active,
                        }))
                      }
                      value={form.active}
                    />
                  </label>
                </div>
              </section>

              <section className="master-form-group">
                <h3>Area dan Rute</h3>
                <div className="dialog-form__grid aligned-form-grid">
                  <label>
                    <span>Area Kirim</span>
                    <select
                      onChange={(event) => setAreaForAdd(event.target.value)}
                      required
                      value={form.resolvedArea}
                    >
                      <option value="">Pilih Area Kirim</option>
                      {activeMappings.map((mapping) => (
                        <option key={mapping.id} value={mapping.areaName}>
                          {mapping.areaName}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>Rute hasil mapping</span>
                    <input
                      className="master-readonly-input"
                      placeholder="Otomatis dari Area Kirim"
                      readOnly
                      title={form.resolvedRoute || ""}
                      value={getRouteDisplay(form)}
                    />
                  </label>
                </div>
              </section>

              <section className="master-form-group">
                <h3>Koordinat</h3>
                <CoordinateInputs onChange={setForm} value={form} />

                <div className="coordinate-form-footer">
                  <div>
                    <CoordinateStatusBadge status={form.coordinateStatus} />
                    <span>Latitude dan longitude wajib diisi berpasangan.</span>
                  </div>

                  {String(form.latitude || "").trim() ||
                  String(form.longitude || "").trim() ? (
                    <button
                      className="danger-button--outline"
                      onClick={() =>
                        setForm((current) => ({
                          ...current,
                          latitude: "",
                          longitude: "",
                          coordinateStatus: "MISSING",
                        }))
                      }
                      type="button"
                    >
                      Kosongkan Koordinat
                    </button>
                  ) : null}
                </div>
              </section>
            </div>

            <footer className="dialog-card__actions">
              <button
                className="secondary-button"
                disabled={busy}
                onClick={() => setCreateOpen(false)}
                type="button"
              >
                Batal
              </button>

              <button className="primary-button" disabled={busy} type="submit">
                {busy ? "Menyimpan..." : "Tambah Alamat"}
              </button>
            </footer>
          </form>
        </Dialog>
      )}

      <DataMasterTableShell
        className="dm-table-shell--shipping dm-table-shell--shipping-compact"
        emptyText="Belum ada alamat kirim yang sesuai filter."
        loading={loading}
        mode="viewport"
        rowCount={addresses.length}
      >
        <table className="master-table master-table--shipping-compact">
          <thead>
            <tr>
              <SortableHeader column="customerName" sort={sort} toggleSort={toggleSort}>
                Pelanggan
              </SortableHeader>
              <SortableHeader column="addressAlias" sort={sort} toggleSort={toggleSort}>
                Alamat tujuan
              </SortableHeader>
              <SortableHeader column="resolvedArea" sort={sort} toggleSort={toggleSort}>
                Area & rute
              </SortableHeader>
              <SortableHeader column="latitude" sort={sort} toggleSort={toggleSort}>
                Koordinat
              </SortableHeader>
              <SortableHeader column="active" sort={sort} toggleSort={toggleSort}>
                Status
              </SortableHeader>
              <th className="sticky-action-cell master-action-cell">Aksi</th>
            </tr>
          </thead>

          <tbody>
            {!loading &&
              addresses.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="dm-cell-stack dm-cell-stack--primary">
                      <strong>{row.customerName}</strong>
                      <span>{row.customerNo || "Tanpa nomor pelanggan"}</span>
                    </div>
                  </td>
                  <td>
                    <div className="dm-cell-stack dm-cell-stack--primary">
                      <strong>{row.addressAlias}</strong>
                      <span>{row.addressText || row.keywordText || "Alamat belum dilengkapi"}</span>
                      {row.keywordText && row.addressText && <small>Keyword: {row.keywordText}</small>}
                    </div>
                  </td>
                  <td>
                    <div className="dm-cell-stack">
                      <strong>{row.resolvedArea || "Belum dipetakan"}</strong>
                      <span>{row.resolvedRouteName || row.resolvedRoute || "Rute belum tersedia"}</span>
                    </div>
                  </td>
                  <td>
                    <div className="dm-location-coordinate-cell">
                      <div className="coordinate-pair-cell">
                        <strong>{formatCoordinate(row.latitude)}</strong>
                        <span>{formatCoordinate(row.longitude)}</span>
                      </div>
                      <div className="coordinate-status-cell">
                        <CoordinateStatusBadge status={row.coordinateStatus} />
                        <small>{row.coordinateSource || "Belum ada sumber"}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="dm-cell-stack">
                      <ActiveBadge active={row.active} />
                      <span>{row.notes || "Tanpa catatan"}</span>
                    </div>
                  </td>
                  <td className="sticky-action-cell master-action-cell">
                    <DataMasterRowActions
                      active={row.active}
                      onDelete={() => setDeleteRow(row)}
                      onEdit={() => setEditRow(mapRowToForm(row))}
                      onToggle={() => toggleActive(row)}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </DataMasterTableShell>

      <DataMasterPagination
        loading={loading}
        onPageChange={changePage}
        onPageSizeChange={changePageSize}
        pagination={pagination}
      />

      {editRow && (
        <Dialog
          className="dialog-card--coordinate-editor"
          onClose={() =>
            setEditRow(null)
          }
          title={`Edit Alamat ${editRow.addressAlias}`}
        >
          <form
            className="dialog-form"
            onSubmit={saveEdit}
          >
            <div className="dialog-card__body grouped-master-form">
              {error && (
                <div className="error-banner" role="alert">
                  {error}
                </div>
              )}

              <section className="master-form-group">
                <h3>Identitas Alamat</h3>

                <div className="dialog-form__grid">
                  <div className="dialog-field dialog-form__full">
                    <span>
                      Nama Pelanggan
                    </span>

                    <CustomerLookup
                      customerNo={
                        editRow.customerNo
                      }
                      onClear={
                        clearCustomerForEdit
                      }
                      onSelect={
                        selectCustomerForEdit
                      }
                      value={
                        editRow.customerName
                      }
                    />
                  </div>

                  <label>
                    <span>Nama Alamat</span>

                    <input
                      onChange={(event) =>
                        setEditRow(
                          (current) => ({
                            ...current,
                            addressAlias:
                              event.target
                                .value,
                          }),
                        )
                      }
                      required
                      value={
                        editRow.addressAlias
                      }
                    />
                  </label>

                  <label className="dialog-form__full">
                    <span>Alamat</span>

                    <textarea
                      onChange={(event) =>
                        setEditRow(
                          (current) => ({
                            ...current,
                            addressText:
                              event.target
                                .value,
                          }),
                        )
                      }
                      rows={3}
                      value={
                        editRow.addressText
                      }
                    />
                  </label>

                  <label>
                    <span>Area Kirim</span>

                    <select
                      onChange={(event) =>
                        setAreaForEdit(
                          event.target.value,
                        )
                      }
                      required
                      value={
                        editRow.resolvedArea
                      }
                    >
                      <option value="">
                        Pilih Area Kirim
                      </option>

                      {activeMappings.map(
                        (mapping) => (
                          <option
                            key={mapping.id}
                            value={
                              mapping.areaName
                            }
                          >
                            {
                              mapping.areaName
                            }
                          </option>
                        ),
                      )}
                    </select>
                  </label>

                  <label>
                    <span>Rute</span>

                    <input
                      className="master-readonly-input"
                      readOnly
                      title={
                        editRow.resolvedRoute ||
                        ""
                      }
                      value={getRouteDisplay(
                        editRow,
                      )}
                    />
                  </label>

                  <label>
                    <span>Keyword</span>

                    <input
                      onChange={(event) =>
                        setEditRow(
                          (current) => ({
                            ...current,
                            keywordText:
                              event.target
                                .value,
                          }),
                        )
                      }
                      value={
                        editRow.keywordText
                      }
                    />
                  </label>

                  <label className="dialog-form__full">
                    <span>Catatan</span>

                    <textarea
                      onChange={(event) =>
                        setEditRow(
                          (current) => ({
                            ...current,
                            notes:
                              event.target
                                .value,
                          }),
                        )
                      }
                      rows={2}
                      value={editRow.notes}
                    />
                  </label>

                  <label>
                    <span>Status</span>

                    <ActiveSelect
                      onChange={(active) =>
                        setEditRow(
                          (current) => ({
                            ...current,
                            active,
                          }),
                        )
                      }
                      value={editRow.active}
                    />
                  </label>
                </div>
              </section>

              <section className="master-form-group">
                <h3>Koordinat Alamat Kirim</h3>

                <CoordinateInputs
                  onChange={setEditRow}
                  value={editRow}
                />

                <div className="coordinate-form-footer">
                  <div>
                    <CoordinateStatusBadge
                      status={
                        editRow.coordinateStatus
                      }
                    />

                    <span>
                      Koordinat tersimpan sebagai
                      snapshot ketika alamat dipilih
                      pada Sales Order.
                    </span>
                  </div>

                  {String(editRow.latitude || "").trim() ||
                  String(editRow.longitude || "").trim() ? (
                    <button
                      className="danger-button--outline"
                      onClick={() =>
                        setEditRow(
                          (current) => ({
                            ...current,
                            latitude: "",
                            longitude: "",
                            coordinateStatus:
                              "MISSING",
                          }),
                        )
                      }
                      type="button"
                    >
                      Kosongkan Koordinat
                    </button>
                  ) : null}
                </div>
              </section>
            </div>

            <ModalActions
              busy={busy}
              onCancel={() =>
                setEditRow(null)
              }
            />
          </form>
        </Dialog>
      )}

      {deleteRow && (
        <ConfirmDialog
          busy={busy}
          description={`Hapus alamat “${deleteRow.addressAlias}” untuk pelanggan “${deleteRow.customerName}”? Tindakan ini tidak dapat dibatalkan.`}
          onCancel={() =>
            setDeleteRow(null)
          }
          onConfirm={confirmDelete}
          title="Hapus Alamat Kirim"
        />
      )}
    </DataMasterSection>
  );
}
